import {
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import SelectSearch from 'components/fields/SelectSearch';

import DateField from 'components/fields/DateField';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import { SectionCard, ValidatedField } from '../../components';
import { useAnswerCodes } from '../../hooks/useAnswerCodes';
import type { PropertyPolicy } from './types';

interface MultiPolicyPropertyProps {
  propertyPolicy: PropertyPolicy;
  isEnabled: boolean;
  onFieldChange: (field: string, value: any) => void;
}

const MultiPolicyProperty = ({
  propertyPolicy,
  isEnabled,
  onFieldChange,
}: MultiPolicyPropertyProps) => {
  const { getOptions, isLoading } = useAnswerCodes();
  const policyTypeOptions = getOptions('habitational_policy_type');
  const insurerOptions = getOptions('current_habitational_insurer');

  const otherPolicyTypeCode = policyTypeOptions.find(
    (opt) => opt.label.toLowerCase() === 'other',
  )?.value;
  const otherInsurerCode = insurerOptions.find(
    (opt) => opt.label.toLowerCase() === 'other',
  )?.value;

  const showOtherPolicyType =
    otherPolicyTypeCode &&
    propertyPolicy.habitational_policy_type === otherPolicyTypeCode;
  const showOtherInsurer =
    otherInsurerCode &&
    propertyPolicy.current_habitational_insurer === otherInsurerCode;

  const handlePropertyChange = (
    field: keyof PropertyPolicy,
    value: string | null,
  ) => {
    onFieldChange('property_policy', {
      ...propertyPolicy,
      [field]: value,
    });
  };

  const validationEntity = {
    property_policy: {
      habitational_policy_type: propertyPolicy.habitational_policy_type,
      other_habitational_policy_type:
        propertyPolicy.other_habitational_policy_type,
      current_habitational_insurer: propertyPolicy.current_habitational_insurer,
      other_current_habitational_insurer:
        propertyPolicy.other_current_habitational_insurer,
    },
  };

  return (
    <VStack spacing={4} align="stretch">
      <SectionCard
        title="Property Policy"
        opacity={isEnabled ? 1 : 0.6}
        pointerEvents={isEnabled ? 'auto' : 'none'}
      >
        {!isEnabled ? (
          <Text fontSize="xs" color="gray.500">
            Enable Multi-Policy Discount to add property details
          </Text>
        ) : (
          <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={3}>
            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Policy Type
                </FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={propertyPolicy.habitational_policy_type || ''}
                  onChange={(e) =>
                    handlePropertyChange(
                      'habitational_policy_type',
                      e.target.value,
                    )
                  }
                >
                  {policyTypeOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>

            {showOtherPolicyType && (
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <ValidatedField
                  itemPath="policy"
                  fieldPath="property_policy.other_habitational_policy_type"
                  label="Other Policy Type"
                  entity={validationEntity}
                >
                  <Input
                    variant="main"
                    size="sm"
                    placeholder="Specify policy type"
                    value={propertyPolicy.other_habitational_policy_type || ''}
                    onChange={(e) =>
                      handlePropertyChange(
                        'other_habitational_policy_type',
                        e.target.value,
                      )
                    }
                  />
                </ValidatedField>
              </GridItem>
            )}

            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Carrier
                </FormLabel>
                <SelectSearch
                  options={insurerOptions}
                  value={propertyPolicy.current_habitational_insurer || ''}
                  onChange={(value) =>
                    handlePropertyChange(
                      'current_habitational_insurer',
                      String(value ?? ''),
                    )
                  }
                  placeholder="Search carrier..."
                  isLoading={isLoading}
                />
              </FormControl>
            </GridItem>

            {showOtherInsurer && (
              <GridItem colSpan={{ base: 1, md: 2 }}>
                <ValidatedField
                  itemPath="policy"
                  fieldPath="property_policy.other_current_habitational_insurer"
                  label="Other Carrier"
                  entity={validationEntity}
                >
                  <Input
                    variant="main"
                    size="sm"
                    placeholder="Specify carrier name"
                    value={
                      propertyPolicy.other_current_habitational_insurer || ''
                    }
                    onChange={(e) =>
                      handlePropertyChange(
                        'other_current_habitational_insurer',
                        e.target.value,
                      )
                    }
                  />
                </ValidatedField>
              </GridItem>
            )}

            <GridItem colSpan={{ base: 1, md: 2 }}>
              <FormControl>
                <FormLabel fontSize="sm" fontWeight="600">
                  Policy Number
                </FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  placeholder="Enter policy number"
                  value={propertyPolicy.habitational_policy_number || ''}
                  onChange={(e) =>
                    handlePropertyChange(
                      'habitational_policy_number',
                      e.target.value,
                    )
                  }
                />
              </FormControl>
            </GridItem>

            <GridItem>
              <DateField
                label="Since"
                date={parseDateString(propertyPolicy.since)}
                onChange={(date: Date | null) =>
                  handlePropertyChange('since', formatDateToString(date))
                }
                size="sm"
              />
            </GridItem>

            <GridItem>
              <DateField
                label="Occupied Since"
                date={parseDateString(propertyPolicy.occupied_since)}
                onChange={(date: Date | null) =>
                  handlePropertyChange(
                    'occupied_since',
                    formatDateToString(date),
                  )
                }
                size="sm"
              />
            </GridItem>
          </Grid>
        )}
      </SectionCard>
    </VStack>
  );
};

export default MultiPolicyProperty;
